/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MaxWidthLineBreak } from "~/components/ui/max-width-line-break";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { AMAZON_STORE_URL, APP_STORE_URL, GOOGLE_PLAY_URL } from "~/lib/external-links";

/**
 * Ported from bouncieweb's `LandingPageComponents/Ratings.js`.
 *
 * Three app/store rating cards (Apple, Google Play, Amazon), each with a store
 * logo, the numeric rating, a canvas-drawn star bar, and a "N Reviews" link.
 *
 * The original pulled ratings and review counts from Redux
 * (`appRatings.bouncieRating.*`, `productRatings.bouncieRating.*`). Redux is not
 * available in this project, so those values are hardcoded here — see
 * TODO(BNC-5080) below. The Amazon link, which the original derived from Redux
 * config + cart state, is likewise a plain external link to amazon.com/bouncie.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  md 768px -> md:
 */

// TODO(BNC-5080): static — was Redux appRatings / productRatings. Rule 9
// specifies googleRating 4.7 / googleReviewCount 2500 as the fallback; the same
// values are applied to the Apple and Amazon cards (original Redux default was 0).
const APPLE_RATING = 4.7;
const APPLE_REVIEW_COUNT = 2500;
const GOOGLE_RATING = 4.7;
const GOOGLE_REVIEW_COUNT = 2500;
const AMAZON_RATING = 4.7;
const AMAZON_REVIEW_COUNT = 2500;

const ICON_WIDTH = 36;

/**
 * Ported from bouncieweb's `StarRating` (canvas-drawn 5-star bar with a partial
 * fill representing the fractional rating). Canvas is browser-only, so all
 * drawing happens inside effects; SSR renders an empty <canvas>.
 */
export function StarRating({
  rating = 0,
  activeFill = "#FAD20C",
  inactiveFill = colors.grayLight1,
  numberOfStars = 5,
  className,
}: {
  rating?: number;
  activeFill?: string;
  inactiveFill?: string;
  numberOfStars?: number;
  className?: string;
}) {
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);

  const drawStar = useCallback(
    (cx: number, cy: number, outerRadius: number, innerRadius: number) => {
      const ctx = contextRef.current;
      if (!ctx) return;
      const spikes = 5;
      const starWidth = containerWidth / numberOfStars;
      let rot = (Math.PI / 2) * 3;
      let x = cx;
      let y = cy;
      const step = Math.PI / spikes;
      const gradient = ctx.createLinearGradient(
        starWidth * rating - 0.1,
        0,
        starWidth * rating,
        0,
      );
      gradient.addColorStop(0, activeFill);
      gradient.addColorStop(1, inactiveFill);

      ctx.beginPath();
      for (let i = 0; i < spikes; i += 1) {
        x = cx + Math.cos(rot) * outerRadius;
        y = cy + Math.sin(rot) * outerRadius;
        ctx.lineTo(x, y);
        rot += step;

        x = cx + Math.cos(rot) * innerRadius;
        y = cy + Math.sin(rot) * innerRadius;
        ctx.lineTo(x, y);
        rot += step;
      }
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
    },
    [activeFill, containerWidth, inactiveFill, numberOfStars, rating],
  );

  // Measure the container on mount and on resize. The original ran this on
  // every render (deps-less effect); measuring on mount + resize is the
  // equivalent intent — capture the laid-out width so the star canvas can be
  // sized — without risking a render loop. setContainerWidth bails out when the
  // measured width is unchanged.
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth * 2);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    if (!containerWidth) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio;
    const starWidth = containerWidth / 2 / numberOfStars;
    const arr = new Array(numberOfStars).fill(0);

    canvas.width = containerWidth * dpr;
    canvas.height = (containerWidth / numberOfStars) * 0.9 * dpr;

    const ctx = canvas.getContext("2d");
    contextRef.current = ctx;
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    arr.forEach((_, index) => {
      drawStar(
        index * (starWidth * 2) + starWidth,
        starWidth,
        starWidth,
        starWidth * 0.39,
      );
    });
  }, [containerWidth, drawStar, numberOfStars]);

  return (
    <div ref={containerRef} className={className}>
      <canvas ref={canvasRef} className="block w-full" />
    </div>
  );
}

/** Ported from `AppleLogo` -> `WrappedSVGs/AppleLogo`. */
function AppleLogo({
  width = 48,
  color = colors.black,
}: {
  width?: number;
  color?: string;
}) {
  return (
    <div className="align-middle">
      <svg
        width={width}
        height={width * (60 / 48)}
        viewBox="0 0 48 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M46.467 20.456C46.125 20.7261 40.0875 24.1884 40.0875 31.8872C40.0875 40.7921 47.7701 43.9424 48 44.0204C47.9646 44.2124 46.7795 48.3348 43.9494 52.5353C41.4259 56.2316 38.7903 59.922 34.781 59.922C30.7717 59.922 29.7398 57.5518 25.1114 57.5518C20.6009 57.5518 18.9972 60 15.3298 60C11.6624 60 9.10355 56.5797 6.16141 52.3792C2.75347 47.4467 0 39.784 0 32.5112C0 20.8461 7.45265 14.6595 14.7874 14.6595C18.6847 14.6595 21.9334 17.2637 24.3803 17.2637C26.7092 17.2637 30.3412 14.5035 34.7751 14.5035C36.4555 14.5035 42.4931 14.6595 46.467 20.456ZM32.6702 9.56496C34.5039 7.35073 35.801 4.27843 35.801 1.20612C35.801 0.780078 35.7656 0.348035 35.689 0C32.7056 0.114011 29.1561 2.0222 27.0158 4.54845C25.3355 6.49265 23.7671 9.56496 23.7671 12.6793C23.7671 13.1473 23.8438 13.6154 23.8791 13.7654C24.0678 13.8014 24.3744 13.8434 24.681 13.8434C27.3578 13.8434 30.7245 12.0192 32.6702 9.56496Z"
          fill={color}
        />
      </svg>
    </div>
  );
}

/** Ported from `GooglePlayLogo` -> `WrappedSVGs/GooglePlayLogo`. */
function GooglePlayLogo({ width = 57 }: { width?: number }) {
  return (
    <div className="align-middle">
      <svg
        width={width}
        height={width * (60 / 57)}
        viewBox="0 0 57 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M41.4195 39.2433C48.5241 35.3427 53.9571 32.3709 54.4215 32.1387C55.9074 31.3493 57.4398 29.2597 54.4215 27.6344C53.4463 27.1236 48.1991 24.2446 41.4195 20.5298L32.0859 29.9098L41.4195 39.2433Z"
          fill="#FFD400"
        />
        <path
          d="M32.089 29.9102L2.27734 59.954C2.97388 60.0468 3.76328 59.8611 4.69199 59.3503C6.64229 58.2823 27.3526 46.952 41.4225 39.2437L32.089 29.9102Z"
          fill="#FF3333"
        />
        <path
          d="M32.0886 29.9096L41.4221 20.5296C41.4221 20.5296 6.78116 1.63035 4.69155 0.515892C3.90215 0.0515363 3.01987 -0.0877705 2.23047 0.0515363L32.0886 29.9096Z"
          fill="#48FF48"
        />
        <path
          d="M32.087 29.9094L2.22891 0.0512695C1.02158 0.329883 0 1.3979 0 3.58038C0 7.06305 0 53.4986 0 56.4241C0 58.4208 0.789406 59.8603 2.27534 59.9996L32.087 29.9094Z"
          fill="#3BCCFF"
        />
      </svg>
    </div>
  );
}

/** Ported from `AmazonLogoMini` -> `WrappedSVGs/AmazonLogoMini`. */
function AmazonLogoMini({ width = 58 }: { width?: number }) {
  return (
    <div className="align-middle">
      <svg
        width={width}
        height={width * (59 / 58)}
        viewBox="0 0 58 59"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M50.6545 48.4412C26.3666 60.0002 11.2933 50.3291 1.64437 44.4551C1.0473 44.0849 0.0324818 44.5417 0.912971 45.553C4.12751 49.4507 14.6622 58.8453 28.4131 58.8453C42.1734 58.8453 50.3596 51.337 51.3836 50.0272C52.4007 48.7286 51.6823 48.0122 50.6543 48.4412H50.6545ZM57.4757 44.6741C56.8235 43.8248 53.5097 43.6665 51.4242 43.9227C49.3355 44.1715 46.2004 45.448 46.4729 46.2145C46.6128 46.5016 46.8983 46.3728 48.3332 46.2438C49.772 46.1002 53.8026 45.5916 54.6425 46.6895C55.4862 47.795 53.3569 53.0613 52.968 53.9106C52.5923 54.76 53.1116 54.979 53.8174 54.4133C54.5134 53.8479 55.7736 52.3835 56.6192 50.3111C57.4591 48.2275 57.9715 45.3208 57.4757 44.6741Z"
          fill="#FF9900"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M34.3237 24.855C34.3237 27.8879 34.4003 30.4172 32.8673 33.1106C31.63 35.3006 29.6701 36.6473 27.4803 36.6473C24.491 36.6473 22.7501 34.3698 22.7501 31.0085C22.7501 24.3731 28.6954 23.1688 34.3237 23.1688V24.855ZM42.1742 43.8301C41.6595 44.2899 40.915 44.3229 40.3348 44.0162C37.7508 41.8702 37.2908 40.8739 35.8675 38.8262C31.5972 43.1841 28.5752 44.487 23.0349 44.487C16.4872 44.487 11.3848 40.4467 11.3848 32.3552C11.3848 26.0376 14.8121 21.7344 19.6842 19.6323C23.9108 17.7707 29.8125 17.4422 34.3237 16.9278V15.9204C34.3237 14.0698 34.4658 11.88 33.382 10.2815C32.4293 8.84713 30.6118 8.25583 29.013 8.25583C26.0459 8.25583 23.3961 9.7777 22.7501 12.9311C22.6186 13.632 22.1041 14.3219 21.4034 14.3546L13.8483 13.5445C13.2134 13.4019 12.5127 12.8875 12.6878 11.9128C14.4287 2.75941 22.6952 0 30.0968 0C33.8854 0 38.8343 1.00743 41.8236 3.87623C45.6122 7.41273 45.2507 12.1318 45.2507 17.2671V29.3989C45.2507 33.045 46.7617 34.6435 48.1851 36.6145C48.6887 37.3152 48.7983 38.1586 48.1631 38.6838C46.5755 40.0086 43.7505 42.4724 42.1959 43.8521L42.174 43.8301"
          fill="black"
        />
      </svg>
    </div>
  );
}

const starRatingClass =
  "h-[27px] mx-auto max-w-[80px] min-[431px]:max-w-[108px]";
const reviewLinkClass =
  "flex items-center justify-center text-[#1F69F2] text-[14px]";

function RatingGridComponent({
  image,
  rating,
  link,
}: {
  image: React.ReactNode;
  rating: number;
  link: React.ReactNode;
}) {
  return (
    // RatingGridItem
    <div className="flex-1 justify-center text-center">
      {/* LogoAndNumber */}
      <div
        className={cn(
          "flex h-[5.4rem] flex-col",
          "min-[431px]:mb-[5px] min-[431px]:h-auto min-[431px]:flex-row min-[431px]:items-center min-[431px]:justify-center min-[431px]:gap-4",
        )}
      >
        {image}
        {/* RatingNumber */}
        <div className="text-[24px] font-bold min-[431px]:text-[21px]">
          {rating ? rating.toFixed(1) : null}
        </div>
      </div>
      <StarRating
        rating={rating}
        activeFill={colors.orange}
        inactiveFill="#aaa"
        className={starRatingClass}
      />
      {link}
    </div>
  );
}

export default function Ratings() {
  return (
    // RatingFlex
    <div
      className={cn(
        "flex flex-wrap items-center gap-4 [row-gap:3rem]",
        "md:mx-auto md:max-w-[70%] md:gap-8",
      )}
    >
      <RatingGridComponent
        image={<AppleLogo width={ICON_WIDTH} />}
        rating={APPLE_RATING}
        link={
          <a
            data-sid="ratings-app-store"
            href={`${APP_STORE_URL}?see-all=reviews`}
            target="_blank"
            rel="noreferrer"
            className={reviewLinkClass}
          >
            {APPLE_REVIEW_COUNT.toLocaleString("en-US")}
            <MaxWidthLineBreak maxWidth={430} />
            Reviews
          </a>
        }
      />
      <RatingGridComponent
        image={<GooglePlayLogo width={ICON_WIDTH} />}
        rating={GOOGLE_RATING}
        link={
          <a
            data-sid="ratings-google-play"
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noreferrer"
            className={reviewLinkClass}
          >
            {GOOGLE_REVIEW_COUNT.toLocaleString("en-US")}
            <MaxWidthLineBreak maxWidth={430} />
            Reviews
          </a>
        }
      />
      <RatingGridComponent
        image={<AmazonLogoMini width={ICON_WIDTH} />}
        rating={AMAZON_RATING}
        link={
          <a
            data-sid="ratings-amazon"
            href={AMAZON_STORE_URL}
            target="_blank"
            rel="noreferrer"
            className={reviewLinkClass}
          >
            {AMAZON_REVIEW_COUNT.toLocaleString("en-US")}
            <MaxWidthLineBreak maxWidth={430} />
            Reviews
          </a>
        }
      />
    </div>
  );
}
